/**
 * Label component - displays the currently selected segment text.
 *
 * Exposes an imperative `setSegment` handle so the parent can update text +
 * background per Winwheel tick without going through React state. Per-frame
 * `setState` on Safari/WebKit causes cascading style recalc + composite that
 * dominates the spin animation; mutating the two DOM nodes directly avoids it.
 */

import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import type { SpinnerWheelLabelProps, SpinnerWheelLabelHandle } from '../types/wheel';
import './SpinnerWheelLabel.css';

export const SpinnerWheelLabel = forwardRef<SpinnerWheelLabelHandle, SpinnerWheelLabelProps>(
  function SpinnerWheelLabel(
    { text, backgroundColor, showWinner = false, variant = 'white', className = '' },
    ref
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLSpanElement>(null);
    const promptRef = useRef<HTMLSpanElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        setSegment(newText: string, newBg: string) {
          const el = containerRef.current;
          if (el) {
            // Only background-color is written per tick. box-shadow is
            // suppressed by `.is-spinning` CSS during a spin, so writing it
            // here every frame would only churn the style/composite pipeline
            // without changing pixels. React will reapply the proper shadow
            // when the spin ends and the component next renders.
            if (el.style.backgroundColor !== newBg) {
              el.style.backgroundColor = newBg || '';
            }
            const hasText = !!newText;
            if (el.classList.contains('--has-text') !== hasText) {
              el.classList.toggle('--has-text', hasText);
            }
          }
          const textEl = textRef.current;
          if (textEl && textEl.textContent !== newText) {
            textEl.textContent = newText || '';
            textEl.style.display = newText ? '' : 'none';
          }
          const promptEl = promptRef.current;
          if (promptEl) {
            const wantHidden = !!newText;
            const isHidden = promptEl.style.display === 'none';
            if (wantHidden !== isHidden) {
              promptEl.style.display = wantHidden ? 'none' : '';
            }
          }
        },
      }),
      []
    );

    const classNames = [
      'spinner-wheel-label',
      `--${variant}`,
      text ? '--has-text' : '',
      showWinner ? '--winning' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const style: React.CSSProperties = backgroundColor
      ? {
          backgroundColor,
          boxShadow: `0 4px 20px ${backgroundColor}40`,
        }
      : {};

    return (
      <div ref={containerRef} className={classNames} style={style}>
        <span
          ref={textRef}
          className="spinner-wheel-label__text"
          style={{ display: text ? undefined : 'none' }}
        >
          {text}
        </span>
        <span
          ref={promptRef}
          className="spinner-wheel-label__prompt"
          style={{ display: text ? 'none' : undefined }}
        >
          Press &#9658; to spin the wheel
        </span>
      </div>
    );
  }
);

export default SpinnerWheelLabel;
